<!-- Заголовок приложения - Organism -->
<template>
  <header class="backdrop-blur-[25px] bg-black/10 fixed top-0 left-0 right-0 z-50 h-16">
    <!-- Разделительная линия под header'ом -->
    <div class="absolute bottom-0 left-0 right-0 h-px">
      <div
        class="absolute bottom-[-0.5px] left-0 right-0 top-[-0.5px] bg-gradient-to-r from-transparent via-white/25 to-transparent">
      </div>
    </div>

    <div class="relative h-full w-full max-w-[1440px] mx-auto">
      <!-- Используем grid для правильного центрирования -->
      <div class="header-grid grid items-center h-full px-6 md:px-[132px]">
        
        <!-- Логотип -->
        <div class="flex justify-start">
          <router-link to="/" class="flex items-center hover:opacity-80 transition-opacity h-[33px] w-[144px]"
            aria-label="На главную">
            <img src="/images/touch-logo.svg" alt="TOUCH" class="h-full w-full object-contain" loading="eager" />
          </router-link>
        </div>

        <!-- Навигация для десктопа - центрированная -->
        <nav class="desktop-nav" role="navigation">
          <a v-for="item in navigationItems" :key="item.href" :href="item.href"
            class="text-[#ececec] text-[15px] font-normal leading-none hover:opacity-80 transition-opacity whitespace-nowrap"
            style="font-family: 'Gotham', sans-serif;"
            @click.prevent="scrollToSection(item.href)">
            {{ item.label }}
          </a>
        </nav>

        <!-- Кнопка действия для десктопа -->
        <div class="desktop-cta">
          <BaseButton tone="light-blue" size="figma" class="btn-header-cta hover:opacity-90" @click="handleCTAClick">
            {{ ctaText }}
            <template #arrow>
              <div class="flex items-center justify-center color-black">
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 11 10" fill="none">
                  <path
                    d="M4.63393 0.772284L7.94942 4.08778H0.000109958L0.000109832 5.17982L7.94942 5.17982L4.63393 8.49531L5.40623 9.26762L10.04 4.6338L5.40623 -1.91303e-05L4.63393 0.772284Z"
                    fill="black" />
                </svg>
              </div>
            </template>
          </BaseButton>
        </div>

        <!-- Гамбургер-меню кнопка (для мобильных и небольших планшетов) -->
        <button class="hamburger-button" 
          :aria-expanded="mobileMenuOpen" 
          aria-label="Открыть меню" 
          @click="toggleMobileMenu">
          <div class="hamburger-menu">
            <span :class="{ 'open': mobileMenuOpen }"></span>
            <span :class="{ 'open': mobileMenuOpen }"></span>
            <span :class="{ 'open': mobileMenuOpen }"></span>
          </div>
        </button>
      </div>
    </div>

    <!-- Мобильная навигация с эффектом стекла -->
    <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0" leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 -translate-y-2">
      <nav v-if="mobileMenuOpen" class="mobile-menu px-6 py-4"
        role="navigation">
        <div class="flex flex-col space-y-4">
          <a v-for="item in navigationItems" :key="item.href" :href="item.href"
            class="text-[#ececec] text-[15px] font-normal leading-none hover:opacity-80 transition-opacity py-2"
            style="font-family: 'Gotham', sans-serif;" 
            @click.prevent="scrollToSection(item.href); closeMobileMenu()">
            {{ item.label }}
          </a>

          <!-- Мобильная CTA кнопка -->
          <div class="pt-4">
            <BaseButton tone="light-blue" size="figma" class="w-full btn-header-cta hover:opacity-90"
              @click="handleCTAClick">
              {{ ctaText }}
              <template #arrow>
                <div class="flex items-center justify-center rotate-45">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 17l9.2-9.2M17 17V7H7" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </div>
              </template>
            </BaseButton>
          </div>
        </div>
      </nav>
    </Transition>
  </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import BaseButton from '@/components/atoms/BaseButton.vue'

interface NavItem {
  label: string
  href: string
  active: boolean
}

interface Props {
  /** Текст CTA кнопки */
  ctaText?: string
}

// Определяем props с дефолтными значениями
const { ctaText = 'Начать карьеру' } = defineProps<Props>()

const emit = defineEmits<{
  ctaClick: []
}>()

const mobileMenuOpen = ref(false)

// Элементы навигации (из дизайна Figma)
const navigationItems: NavItem[] = [
  { label: 'Возможности', href: '#features-section', active: false },
  { label: 'Приложение', href: '#app', active: false },
  { label: 'Кейсы', href: '#cases', active: false },
  { label: 'Тарифы', href: '#pricing', active: false },
  { label: 'FAQ', href: '#faq', active: false }
]

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

const handleCTAClick = () => {
  closeMobileMenu()
  emit('ctaClick')
}

// Плавная прокрутка к секции с учетом высоты header
const scrollToSection = (href: string) => {
  const sectionId = href.replace('#', '')
  const element = document.getElementById(sectionId)
  
  if (element) {
    const headerOffset = 64 // Высота фиксированного header (4rem = 64px)
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }
}

// Закрываем мобильное меню при клике вне его
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('header') && mobileMenuOpen.value) {
    closeMobileMenu()
  }
}

// Закрываем мобильное меню при изменении размера экрана
const handleResize = () => {
  if (window.innerWidth >= 900 && mobileMenuOpen.value) {
    closeMobileMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
/* Адаптивная сетка для header */
.header-grid {
  grid-template-columns: 1fr auto;
}

@media (min-width: 900px) {
  .header-grid {
    grid-template-columns: 1fr auto 1fr;
  }
}

/* Навигация для десктопа */
.desktop-nav {
  display: none;
  align-items: center;
  gap: 50px;
}

@media (min-width: 900px) {
  .desktop-nav {
    display: flex;
  }
}

/* Кнопка CTA для десктопа */
.desktop-cta {
  display: none;
  justify-content: flex-end;
}

@media (min-width: 900px) {
  .desktop-cta {
    display: flex;
  }
}

/* Гамбургер кнопка */
.hamburger-button {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
}

@media (min-width: 900px) {
  .hamburger-button {
    display: none;
  }
}

/* Мобильное меню */
.mobile-menu {
  display: block;
}

@media (min-width: 900px) {
  .mobile-menu {
    display: none;
  }
}

/* Гамбургер-меню в стиле header */
.hamburger-menu {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 28px;
  height: 20px;
  cursor: pointer;
  position: relative;
}

.hamburger-menu span {
  display: block;
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%);
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  transform-origin: center;
}

/* Анимация для открытого состояния */
.hamburger-menu span:nth-child(1).open {
  transform: translateY(9px) rotate(45deg);
}

.hamburger-menu span:nth-child(2).open {
  opacity: 0;
  transform: scaleX(0);
}

.hamburger-menu span:nth-child(3).open {
  transform: translateY(-9px) rotate(-45deg);
}

/* Эффект при наведении */
.hamburger-menu:hover span {
  background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 100%);
}

/* Дополнительный эффект свечения */
.hamburger-menu span::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, rgba(151, 214, 223, 0.3) 0%, rgba(255, 63, 26, 0.3) 100%);
  border-radius: 2px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.hamburger-menu:hover span::after {
  opacity: 1;
}

/* Эффект стекла для мобильного меню */
.mobile-menu {
  position: relative;
  backdrop-filter: blur(40px) saturate(180%);
  -webkit-backdrop-filter: blur(40px) saturate(180%);
  background: rgba(0, 0, 0, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
}

/* Дополнительный внутренний блик для эффекта стекла */
.mobile-menu::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(255, 255, 255, 0.3) 50%, 
    transparent 100%
  );
  pointer-events: none;
}
</style>
